/**
 * The account's FHIR server connections, as the screens and the shell see them
 * (FR-007 to FR-017).
 *
 * Connections and settings are server state, so each lives in the query cache
 * under one key: the list, the switcher in the navbar and the degraded-state
 * banners in the shell all read the same cached answer, and so can never
 * disagree about which server is active or what it refuses.
 *
 * Activating a connection is a settings write followed by a capability check,
 * because that is what the contract says it is (FR-012, FR-016); there is no
 * switch route.
 *
 * @author John Grimes
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiJson, apiVoid } from "../../lib/api/apiClient";
import { plainErrorOf } from "../auth/useSession";

import type { QueryClient, UseMutationResult } from "@tanstack/react-query";
import type {
  AuthoriseResponse,
  CreateConnectionRequest,
  PlainError,
  Settings,
  UpdateConnectionRequest,
} from "shared/api";
import type {
  CapabilityFlags,
  ConnectionStatus,
  ConnectionSummary,
} from "shared/connection";

/** The key the account's connections are cached under. */
export const CONNECTIONS_QUERY_KEY = ["connections"];

/** The key the account's settings are cached under. */
export const SETTINGS_QUERY_KEY = ["settings"];

/** How each authorisation state is named where the user reads it (FR-007). */
export const STATUS_LABELS: Record<ConnectionStatus, string> = {
  unauthenticated: "No sign-in needed",
  pending: "Not connected yet",
  authorised: "Connected",
  needs_reauth: "Needs signing in again",
};

/** What the app cannot do on a connection's server (FR-016, FR-017). */
export type ConnectionLimits = {
  cannotStoreViews: boolean;
  cannotRunPreviews: boolean;
  cannotSave: boolean;
};

/**
 * Reads what a server refuses from its cached capability flags. A flag that has
 * not been checked yet refuses nothing: an unchecked server must not be
 * presented as a broken one (Principle V).
 *
 * @param connection - The connection, when there is an active one.
 * @returns The limits to explain.
 *
 * @example
 * ```ts
 * connectionLimits(active).cannotRunPreviews; // true when $sql-run is absent
 * ```
 */
export const connectionLimits = (
  connection?: ConnectionSummary,
): ConnectionLimits => ({
  cannotStoreViews: connection?.capability?.viewDefinitionStore === false,
  cannotRunPreviews: connection?.capability?.sqlRun === false,
  cannotSave: connection?.readOnly === true,
});

/**
 * Why a stored view or pipeline cannot be deleted on a connection, if it
 * cannot. The server has to declare the `delete` interaction for the resource
 * type that holds it - ViewDefinition for a view, Library for a pipeline's
 * container - and a declaration still missing once the server has been asked
 * again (`useDeleteReason`) counts as absent. A connection whose sign-in
 * refuses writes cannot delete either (FR-017).
 *
 * @param connection - The connection the thing is stored on.
 * @param noun - What would be deleted.
 * @returns The reason the Delete control carries, or undefined when deleting
 *   is allowed.
 *
 * @example
 * ```ts
 * cannotDelete(active, "pipeline");
 * // "This server doesn't say it allows pipelines to be deleted, so the app won't try."
 * ```
 */
export const cannotDelete = (
  connection: ConnectionSummary,
  noun: "view" | "pipeline",
): string | undefined => {
  const capability = connection.capability;
  const declared =
    noun === "view"
      ? capability?.viewDefinitionDelete
      : capability?.libraryDelete;
  if (declared !== true) {
    return `This server doesn't say it allows ${noun}s to be deleted, so the app won't try.`;
  }
  if (connection.readOnly) {
    return `Your sign-in to this server doesn't allow changes, so the ${noun} can't be deleted.`;
  }
  return undefined;
};

/**
 * Names the server behind a base URL, for the places where a sentence reads
 * better with the host than with the whole address.
 *
 * @param baseUrl - The connection's base URL.
 * @returns The host, or the address itself when it cannot be read as a URL.
 *
 * @example
 * ```ts
 * hostOf("https://fhir.stanton.health/r4"); // "fhir.stanton.health"
 * ```
 */
export const hostOf = (baseUrl: string): string => {
  try {
    return new URL(baseUrl).host;
  } catch {
    return baseUrl;
  }
};

/** What the connections hook reports. */
export type ConnectionsState = {
  connections: ConnectionSummary[];
  isPending: boolean;
  error?: PlainError;
};

/**
 * Lists the account's connections.
 *
 * @returns The connections state.
 *
 * @example
 * ```tsx
 * const { connections, isPending } = useConnections();
 * ```
 */
export const useConnections = (): ConnectionsState => {
  const { data, isPending, error } = useQuery({
    queryKey: CONNECTIONS_QUERY_KEY,
    queryFn: () => apiJson<ConnectionSummary[]>("/connections"),
    retry: false,
  });

  return {
    connections: data ?? [],
    isPending,
    ...(error === null ? {} : { error: plainErrorOf(error) }),
  };
};

/** What the settings hook reports. */
export type SettingsState = {
  settings?: Settings;
  isPending: boolean;
  error?: PlainError;
};

/**
 * Reads the account's settings, which is where the active connection lives
 * (FR-012).
 *
 * @returns The settings state.
 *
 * @example
 * ```tsx
 * const { settings } = useSettings();
 * const activeId = settings?.activeConnectionId;
 * ```
 */
export const useSettings = (): SettingsState => {
  const { data, isPending, error } = useQuery({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: () => apiJson<Settings>("/settings"),
    retry: false,
  });

  return {
    ...(data === undefined ? {} : { settings: data }),
    isPending,
    ...(error === null ? {} : { error: plainErrorOf(error) }),
  };
};

/** What the active connection hook reports. */
export type ActiveConnectionState = {
  /** The connection the account is working with, once both have arrived. */
  active?: ConnectionSummary;
  /** The account's settings, once they have arrived. */
  settings?: Settings;
  /** True while the connections or the settings are still arriving. */
  isPending: boolean;
};

/**
 * The connection the account is working with: the one its settings name,
 * found among its connections (FR-012). Every screen that works against a
 * server starts here.
 *
 * @returns The active connection, the settings, and whether either is still
 *   arriving.
 *
 * @example
 * ```tsx
 * const { active, settings, isPending } = useActiveConnection();
 * ```
 */
export const useActiveConnection = (): ActiveConnectionState => {
  const { connections, isPending: connectionsPending } = useConnections();
  const { settings, isPending: settingsPending } = useSettings();
  const active = connections.find(
    (connection) => connection.id === settings?.activeConnectionId,
  );
  return {
    ...(active === undefined ? {} : { active }),
    ...(settings === undefined ? {} : { settings }),
    isPending: connectionsPending || settingsPending,
  };
};

/**
 * Refreshes the connections and the settings together, because every mutation
 * here can change both: activating writes the settings, removing can clear
 * them, and authorising changes a connection's state.
 *
 * @param queryClient - The cache to invalidate.
 * @returns Nothing, once both keys are invalidated.
 */
const refreshConnectionState = async (
  queryClient: QueryClient,
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY }),
  ]);
};

/**
 * Adds a connection. The backend discovers what the server needs and either
 * registers a client with it or uses the credentials the user supplied
 * (FR-008, FR-009).
 *
 * @returns The mutation, whose result is the connection the backend created.
 *
 * @example
 * ```tsx
 * const create = useCreateConnection();
 * create.mutate({ baseUrl: "https://fhir.example.org/r4" });
 * ```
 */
export const useCreateConnection = (): UseMutationResult<
  ConnectionSummary,
  Error,
  CreateConnectionRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateConnectionRequest) =>
      apiJson<ConnectionSummary>("/connections", {
        method: "POST",
        body: request,
      }),
    onSuccess: () => refreshConnectionState(queryClient),
  });
};

/** What an edit sends: the connection to change, and the fields that changed. */
export type ConnectionUpdate = {
  id: string;
  changes: UpdateConnectionRequest;
};

/**
 * Changes a connection's name or its credentials. Changing the credentials
 * resets it to `pending`, so it has to be authorised again.
 *
 * @returns The mutation, whose result is the updated connection.
 *
 * @example
 * ```tsx
 * const update = useUpdateConnection();
 * update.mutate({ id, changes: { name: "Stanton Health R4" } });
 * ```
 */
export const useUpdateConnection = (): UseMutationResult<
  ConnectionSummary,
  Error,
  ConnectionUpdate
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: ConnectionUpdate) =>
      apiJson<ConnectionSummary>(`/connections/${update.id}`, {
        method: "PATCH",
        body: update.changes,
      }),
    onSuccess: () => refreshConnectionState(queryClient),
  });
};

/**
 * Removes a connection. The backend clears the active connection when it was
 * this one, so the settings are refreshed alongside the list (spec.md edge
 * cases).
 *
 * @returns The mutation.
 *
 * @example
 * ```tsx
 * const remove = useRemoveConnection();
 * remove.mutate(connection);
 * ```
 */
export const useRemoveConnection = (): UseMutationResult<
  void,
  Error,
  ConnectionSummary
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connection: ConnectionSummary) =>
      apiVoid(`/connections/${connection.id}`, { method: "DELETE" }),
    onSuccess: () => refreshConnectionState(queryClient),
  });
};

/** What starting an authorisation needs. */
export type AuthoriseInput = {
  connection: ConnectionSummary;
  /** The SPA path to come back to once the round trip finishes (FR-058). */
  returnPath: string;
};

/**
 * Asks the backend for the authorisation URL at a server's own authorisation
 * server. The caller sends the browser there with a top-level navigation; the
 * app never fetches it, because the page belongs to that server and fetching it
 * would neither show it nor set its cookies (FR-008, FR-010, Principle I).
 *
 * @returns The mutation, whose result carries the redirect URL.
 *
 * @example
 * ```tsx
 * const authorise = useAuthoriseConnection();
 * authorise.mutate(
 *   { connection, returnPath: "/views/abc" },
 *   { onSuccess: ({ redirectUrl }) => { location.assign(redirectUrl); } },
 * );
 * ```
 */
export const useAuthoriseConnection = (): UseMutationResult<
  AuthoriseResponse,
  Error,
  AuthoriseInput
> =>
  useMutation({
    mutationFn: (input: AuthoriseInput) =>
      apiJson<AuthoriseResponse>(
        `/connections/${input.connection.id}/authorise`,
        { method: "POST", body: { returnPath: input.returnPath } },
      ),
  });

/**
 * Makes a connection the active one and checks what its server can do.
 *
 * The active connection is a setting, so this is a settings write and nothing
 * more: no re-authorisation is asked for while the backend's session with that
 * server still works (FR-012). The whole settings object goes back, because the
 * route replaces it. The capability check that follows is what decides whether
 * the app is fully usable, degraded, or stopped on this server (FR-016).
 *
 * @returns The mutation, whose result is the server's capability flags.
 *
 * @example
 * ```tsx
 * const activate = useActivateConnection();
 * activate.mutate(connection, { onError: (error) => setFailure(error) });
 * ```
 */
export const useActivateConnection = (): UseMutationResult<
  CapabilityFlags,
  Error,
  ConnectionSummary
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (connection: ConnectionSummary) => {
      // The account's other settings must survive the write, so the current
      // ones are read first, from the cache when it already holds them.
      const current = await queryClient.ensureQueryData({
        queryKey: SETTINGS_QUERY_KEY,
        queryFn: () => apiJson<Settings>("/settings"),
      });
      await apiJson<Settings>("/settings", {
        method: "PUT",
        body: { ...current, activeConnectionId: connection.id },
      });
      return apiJson<CapabilityFlags>(
        `/connections/${connection.id}/capability`,
        { method: "POST", body: {} },
      );
    },
    // The settings write lands even when the capability check that follows
    // fails, so the cache is refreshed either way.
    onSettled: () => refreshConnectionState(queryClient),
  });
};
